import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Container } from 'reactstrap';

interface StepProps {
  number: number;
  icon: React.ReactNode;
  title: string;
  description: string;
  isLast: boolean;
}

const Step: React.FC<StepProps> = ({ number, icon, title, description, isLast }) => {
  const prefersReducedMotion = useReducedMotion();

  return (
    <motion.div
      className="step-item"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay: number * 0.15 }}
    >
      {/* Step number with connector */}
      <div className="step-indicator">
        <motion.div
          className="step-number"
          whileHover={prefersReducedMotion ? {} : { scale: 1.1 }}
        >
          <span className="number-glow" />
          <span className="number-inner">
            {icon}
          </span>
          <span className="number-text">{number}</span>
        </motion.div>
        {!isLast && (
          <motion.div
            className="step-connector"
            initial={{ scaleY: 0 }}
            whileInView={{ scaleY: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: number * 0.15 + 0.3 }}
          />
        )}
      </div>

      {/* Step content */}
      <div className="step-content">
        <h4 className="step-title">{title}</h4>
        <p className="step-description">{description}</p>
      </div>
    </motion.div>
  );
};

const HowItWorks: React.FC = () => {
  const steps = [
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
      ),
      title: "Find Products",
      description: "Enter ASINs or browse Amazon. Our scraper fetches real-time pricing, stock, and product data across 8 Amazon marketplaces.",
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
        </svg>
      ),
      title: "Create Listings with AI",
      description: "GPT-4o generates optimised titles, descriptions, categories, and item specifics. Publish to any of 8 eBay marketplaces in one click.",
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      ),
      title: "Automate Everything",
      description: "Smart repricing, stock sync, auto messages, and Safe Repricing Protection run 24/7. Set your rules and let Coderom handle the rest.",
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
        </svg>
      ),
      title: "Fulfil & Track",
      description: "When orders come in, Automatic Ordering handles Amazon checkout. Tracking numbers are captured from carrier emails and sent to your eBay buyers automatically.",
    },
  ];

  return (
    <section className="how-it-works-section" id="how-it-works">
      <Container>
        {/* Section Header */}
        <motion.div
          className="section-header centered"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6 }}
        >
          <div className="section-badge">
            <span className="badge-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </span>
            <span>How It Works</span>
          </div>
          <h2 className="section-title">
            From product to profit in <span className="gradient-text">4 simple steps</span>
          </h2>
          <p className="section-subtitle">
            Our streamlined workflow gets you selling faster than ever before.
          </p>
        </motion.div>

        {/* Steps Timeline (mobile) */}
        <div className="steps-timeline">
          {steps.map((step, index) => (
            <Step
              key={index}
              number={index + 1}
              {...step}
              isLast={index === steps.length - 1}
            />
          ))}
        </div>

        {/* Horizontal layout for desktop */}
        <div className="steps-horizontal">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              className="step-horizontal"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              {/* Connector */}
              {index < steps.length - 1 && (
                <motion.div
                  className="horizontal-connector"
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: index * 0.1 + 0.3 }}
                />
              )}

              {/* Step number */}
              <div className="step-number-horizontal">
                <span className="number-glow" />
                <span>{index + 1}</span>
              </div>

              {/* Icon */}
              <div className="step-icon-horizontal">{step.icon}</div>

              {/* Content */}
              <h4 className="step-title-horizontal">{step.title}</h4>
              <p className="step-desc-horizontal">{step.description}</p>
            </motion.div>
          ))}
        </div>
      </Container>

      {/* Background */}
      <div className="how-it-works-bg">
        <div className="bg-gradient" />
      </div>
    </section>
  );
};

export default HowItWorks;
